import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowRight,
  UploadCloud,
  X,
  FileText,
  ImageIcon,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  LayoutDashboard,
} from "lucide-react";
import { ASSETS, CONTACT } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCOPE_OPTIONS = [
  "Organizing / Coordinating Marketing Campaigns",
  "Managing Social Media Presence",
  "Planning / Attending Community Events",
  "Creating / Distributing Co-Branded Marketing Materials",
  "Building Partnerships with Real Estate Professionals",
  "Hosting Educational Seminars / Training",
];

const UPLOAD_CATEGORIES = [
  "Email Marketing",
  "SMS / Text Marketing",
  "Social Media Marketing",
  "Digital & Print Materials",
  "Video & Content Creation",
  "Open Houses & Events",
  "Partner & Realtor Outreach",
  "Web & Online Presence",
  "CRM & Platform Activity",
  "Administrative Proof",
];

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const YEARS = ["2025", "2026", "2027", "2028"];

const inputCls =
  "w-full bg-linen/60 border border-linen-border rounded-xl px-4 py-3 text-sm text-navy placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-teal/50 focus:border-teal transition-all";

export default function BDMReport() {
  const now = new Date();
  const [form, setForm] = useState({
    role: "BDM",
    bdm_name: "",
    email: "",
    loan_officer_name: "Jason Gray",
    period_month: MONTHS[now.getMonth()],
    period_year: String(now.getFullYear() < 2025 ? 2026 : now.getFullYear()),
    scope_of_work: [],
    scope_other: "",
    activities: {
      campaigns: "",
      social_media_posts: "",
      events: "",
      materials_created: "",
      partner_promotion: "",
    },
    additional_notes: "",
    certified: false,
    signature: "",
  });
  const [files, setFiles] = useState([]); // {file, category, id}
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const dropRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    document.title = "Monthly Services Verification | Great Rate Check";
    let el = document.head.querySelector('meta[name="robots"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex,nofollow");
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setActivity = (k, v) =>
    setForm((f) => ({ ...f, activities: { ...f.activities, [k]: v } }));
  const toggleScope = (s) =>
    setForm((f) => ({
      ...f,
      scope_of_work: f.scope_of_work.includes(s)
        ? f.scope_of_work.filter((x) => x !== s)
        : [...f.scope_of_work, s],
    }));

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList).map((file) => ({
      file,
      category: UPLOAD_CATEGORIES[2],
      id: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 7)}`,
    }));
    setFiles((prev) => [...prev, ...incoming].slice(0, 25));
  };

  const onDrop = (e) => {
    e.preventDefault();
    dropRef.current?.classList.remove("ring-2", "ring-teal");
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.bdm_name || !form.email || !form.signature) {
      setError("Please fill in your name, email, and signature.");
      return;
    }
    if (!form.certified) {
      setError("Please check the RESPA certification box to submit.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("data", JSON.stringify(form));
      fd.append("categories", JSON.stringify(files.map((f) => f.category)));
      files.forEach((f) => fd.append("files", f.file));
      const { data } = await axios.post(`${API}/bdm-reports`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-linen flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-lg w-full rounded-3xl bg-white border border-linen-border shadow-lift p-10 text-center"
          data-testid="report-success"
        >
          <CheckCircle2 className="mx-auto text-teal" size={48} strokeWidth={1.5} />
          <h1 className="mt-5 font-heading font-medium text-3xl text-navy">Report submitted</h1>
          <p className="mt-3 text-ink-600">
            Thanks, {form.bdm_name.split(" ")[0] || "there"} — your {form.period_month} {form.period_year}{" "}
            verification and {done.uploads} supporting file{done.uploads === 1 ? "" : "s"} were received.
          </p>
          <p className="mt-1 text-sm text-ink-400">Reference ID: {done.id}</p>
          <button
            data-testid="report-another"
            onClick={() => {
              setDone(null);
              setFiles([]);
              setForm((f) => ({ ...f, scope_of_work: [], additional_notes: "", certified: false, signature: "", activities: { campaigns: "", social_media_posts: "", events: "", materials_created: "", partner_promotion: "" } }));
            }}
            className="mt-7 inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold px-6 py-3 rounded-full transition-colors"
          >
            Submit another report
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="bdm-report-page" className="min-h-screen bg-linen text-navy">
      {/* Header band */}
      <header className="grain-bg noise text-white">
        <div className="container-grc py-10 sm:py-14">
          <div className="flex items-start justify-between gap-4">
            <div className="inline-flex items-center rounded-2xl bg-white/95 px-3 py-1.5 shadow-soft">
              <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-8 w-auto" />
            </div>
            {typeof window !== "undefined" && localStorage.getItem("grc_admin_token") ? (
              <a
                href="/admin"
                data-testid="report-back-to-admin"
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 hover:bg-white/10 px-3 py-1.5 text-sm text-white/90"
              >
                <LayoutDashboard className="w-3.5 h-3.5" /> Back to Admin
              </a>
            ) : null}
          </div>
          <p className="mt-6 text-xs font-bold tracking-[0.22em] uppercase text-lime/90">
            NEXA Lending · RESPA Compliance
          </p>
          <h1 className="mt-3 font-heading font-medium text-3xl sm:text-4xl lg:text-5xl text-balance max-w-3xl">
            Monthly Business Development Services Verification
          </h1>
          <p className="mt-4 max-w-2xl text-white/70">
            For W-2 / part-time marketing partners working with mortgage lenders. Log your
            monthly marketing activity and attach supporting documentation (screenshots, photos, PDFs).
          </p>
        </div>
      </header>

      <form onSubmit={submit} data-testid="bdm-report-form" className="container-grc py-10 sm:py-14 max-w-4xl">
        {/* Employee info */}
        <Card title="Your information" step="1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Role">
              <select data-testid="report-role" value={form.role} onChange={(e) => set("role", e.target.value)} className={inputCls}>
                <option value="BDM">Business Development Marketer (Partner)</option>
                <option value="MLO">Mortgage Loan Originator (MLO)</option>
              </select>
            </Field>
            <Field label="Reporting period" required>
              <div className="flex gap-2">
                <select data-testid="report-month" value={form.period_month} onChange={(e) => set("period_month", e.target.value)} className={inputCls}>
                  {MONTHS.map((m) => <option key={m}>{m}</option>)}
                </select>
                <select data-testid="report-year" value={form.period_year} onChange={(e) => set("period_year", e.target.value)} className={inputCls}>
                  {YEARS.map((y) => <option key={y}>{y}</option>)}
                </select>
              </div>
            </Field>
            <Field label="Your full name" required>
              <input data-testid="report-name" value={form.bdm_name} onChange={(e) => set("bdm_name", e.target.value)} className={inputCls} placeholder="Daren Hamilton" />
            </Field>
            <Field label="Your email" required>
              <input data-testid="report-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="you@example.com" />
            </Field>
            <Field label="Loan officer">
              <input data-testid="report-lo" value={form.loan_officer_name} onChange={(e) => set("loan_officer_name", e.target.value)} className={inputCls} />
            </Field>
          </div>
        </Card>

        {/* Scope of work */}
        <Card title="Scope of work" step="2" sub="Check all that apply.">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SCOPE_OPTIONS.map((s, i) => {
              const active = form.scope_of_work.includes(s);
              return (
                <button
                  type="button"
                  key={s}
                  data-testid={`report-scope-${i}`}
                  onClick={() => toggleScope(s)}
                  className={`text-left flex items-start gap-3 rounded-xl border px-4 py-3 text-sm transition-all ${
                    active ? "border-teal bg-teal-light text-navy" : "border-linen-border bg-white text-ink-700 hover:border-teal/50"
                  }`}
                >
                  <span className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 ${active ? "bg-teal border-teal" : "border-ink-300"}`}>
                    {active && <CheckCircle2 size={12} className="text-white" />}
                  </span>
                  {s}
                </button>
              );
            })}
          </div>
          <div className="mt-3">
            <input data-testid="report-scope-other" value={form.scope_other} onChange={(e) => set("scope_other", e.target.value)} className={inputCls} placeholder="Other (please specify)…" />
          </div>
        </Card>

        {/* Activities */}
        <Card title="Marketing & exposure activities" step="3" sub="Briefly describe your work. Put N/A if not applicable.">
          <div className="space-y-4">
            {[
              ["campaigns", "Campaigns"],
              ["social_media_posts", "Social media posts"],
              ["events", "Events hosted / attended"],
              ["materials_created", "Materials created"],
              ["partner_promotion", "Promotion of NEXA to other business partners"],
            ].map(([k, label]) => (
              <Field key={k} label={label}>
                <textarea data-testid={`report-activity-${k}`} rows={2} value={form.activities[k]} onChange={(e) => setActivity(k, e.target.value)} className={`${inputCls} resize-none`} placeholder="Short description…" />
              </Field>
            ))}
          </div>
        </Card>

        {/* Uploads */}
        <Card title="Supporting documentation" step="4" sub="Attach screenshots, photos, or PDFs that show completed work (not intent). Max 25 files, 15MB each.">
          <div
            ref={dropRef}
            data-testid="report-dropzone"
            onDragOver={(e) => { e.preventDefault(); dropRef.current?.classList.add("ring-2", "ring-teal"); }}
            onDragLeave={() => dropRef.current?.classList.remove("ring-2", "ring-teal")}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className="rounded-2xl border-2 border-dashed border-linen-border bg-white hover:border-teal/60 cursor-pointer p-8 text-center transition-colors"
          >
            <UploadCloud className="mx-auto text-teal" size={32} strokeWidth={1.5} />
            <p className="mt-3 font-medium text-navy">Drag & drop files here, or click to browse</p>
            <p className="text-xs text-ink-400 mt-1">PNG, JPG, WEBP, HEIC, GIF, PDF</p>
            <input
              ref={inputRef}
              data-testid="report-file-input"
              type="file"
              multiple
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => e.target.files && addFiles(e.target.files)}
            />
          </div>

          {files.length > 0 && (
            <ul className="mt-4 space-y-2" data-testid="report-file-list">
              {files.map((f, idx) => (
                <li key={f.id} className="flex items-center gap-3 rounded-xl border border-linen-border bg-linen/50 px-3 py-2">
                  <span className="text-teal shrink-0">
                    {f.file.type === "application/pdf" ? <FileText size={18} /> : <ImageIcon size={18} />}
                  </span>
                  <span className="text-sm text-navy truncate flex-1" title={f.file.name}>{f.file.name}</span>
                  <select
                    data-testid={`report-file-category-${idx}`}
                    value={f.category}
                    onChange={(e) => setFiles((prev) => prev.map((x) => (x.id === f.id ? { ...x, category: e.target.value } : x)))}
                    className="text-xs bg-white border border-linen-border rounded-lg px-2 py-1 max-w-[45%]"
                  >
                    {UPLOAD_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                  <button type="button" data-testid={`report-file-remove-${idx}`} onClick={() => setFiles((prev) => prev.filter((x) => x.id !== f.id))} className="text-ink-400 hover:text-destructive shrink-0">
                    <X size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Notes + certification */}
        <Card title="Certification" step="5">
          <Field label="Additional notes (optional)">
            <textarea data-testid="report-notes" rows={3} value={form.additional_notes} onChange={(e) => set("additional_notes", e.target.value)} className={`${inputCls} resize-none`} placeholder="Anything else to add for this period…" />
          </Field>

          <label className="mt-5 flex items-start gap-3 rounded-xl bg-linen/60 border border-linen-border p-4 text-sm text-ink-700">
            <input data-testid="report-certify" type="checkbox" checked={form.certified} onChange={(e) => set("certified", e.target.checked)} className="mt-1 h-4 w-4 rounded border-linen-border text-teal focus:ring-teal" />
            <span>
              I certify the services I have performed and the compensation I have received are for
              <strong className="text-navy"> bona fide marketing and business development services</strong> and
              <strong className="text-navy"> not for the referral of settlement service business</strong>, in compliance with RESPA.
            </span>
          </label>

          <div className="mt-4">
            <Field label="Signature (type your full name)" required>
              <input data-testid="report-signature" value={form.signature} onChange={(e) => set("signature", e.target.value)} className={`${inputCls} font-heading text-lg`} placeholder="Your full name" />
            </Field>
          </div>

          {error && <p data-testid="report-error" className="mt-4 text-sm text-destructive font-medium">{error}</p>}

          <button
            data-testid="report-submit"
            type="submit"
            disabled={submitting}
            className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-lime hover:bg-lime-hover disabled:opacity-60 text-navy-deep font-semibold px-8 py-4 rounded-full shadow-cta hover:shadow-cta-hover transition-all hover:-translate-y-0.5"
          >
            {submitting ? <><Loader2 size={18} className="animate-spin" /> Submitting…</> : <>Submit verification <ArrowRight size={18} /></>}
          </button>
          <p className="mt-4 text-xs text-ink-400 flex items-center gap-2">
            <ShieldCheck size={14} className="text-teal" /> Your submission is transmitted securely and stored confidentially. Questions? {CONTACT.phoneDisplay}
          </p>
        </Card>
      </form>
    </div>
  );
}

function Card({ title, step, sub, children }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5 }}
      className="rounded-3xl bg-white border border-linen-border shadow-soft p-6 sm:p-8 mb-6"
    >
      <div className="flex items-center gap-3 mb-5">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-navy text-lime text-sm font-semibold">{step}</span>
        <div>
          <h2 className="font-heading font-medium text-xl text-navy">{title}</h2>
          {sub && <p className="text-sm text-ink-500">{sub}</p>}
        </div>
      </div>
      {children}
    </motion.section>
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-ink-600">
        {label} {required && <span className="text-teal">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
